import { useEffect, useRef, useState } from "react";
import * as React from "react";
import { primary, misc } from "app/themes/colors";
import { AppointmentModel } from "@devexpress/dx-react-scheduler";
import { AppointmentTooltip as DXAppointmentTooltip } from "@devexpress/dx-react-scheduler-material-ui";
import { styled } from "@mui/material/styles";
import {
  EVENT_TYPE_APPOINTMENT,
  EVENT_TYPE_APPOINTMENT_BLOCK,
  EVENT_TYPE_CLINIC_EVENT,
  EVENT_TYPE_PERSONAL_EVENT,
  EVENT_TYPE_PERSONAL_WORKSHIFT,
} from "constants/appointments";
import { AppointmentBlock, PersonalEvent } from "services/types/events.type";
import AppointmentBlockPreview from "./appointment-block-preview.component";
import AppointmentPreviewAction from "./appointment-preview-action";
import AppointmentPreviewContent from "./appointment-preview-content";
import AppointmentPreviewTitle from "./appointment-preview-title.component";
import PersonalEventPreview from "./personal-event-preview.component";
import {
  convertDateInTimezoneToCurrent,
  formatDateToDateAndTime,
} from "utils/common";
import CreatedByBlock from "./created-by-block.component";

const PREFIX = "Tooltip";
export const classes = {
  flexibleSpace: `${PREFIX}-flexibleSpace`,
  content: `${PREFIX}-content`,
  contentContainer: `${PREFIX}-contentContainer`,
  text: `${PREFIX}-text`,
  title: `${PREFIX}-title`,
  icon: `${PREFIX}-icon`,
  contentItemIcon: `${PREFIX}-contentItemIcon`,
  grayIcon: `${PREFIX}-grayIcon`,
  colorfulContent: `${PREFIX}-colorfulContent`,
  lens: `${PREFIX}-lens`,
  textCenter: `${PREFIX}-textCenter`,
  dateAndTitle: `${PREFIX}-dateAndTitle`,
  titleContainer: `${PREFIX}-titleContainer`,
  container: `${PREFIX}-container`,
};

export const StyledTooltipContent = styled("div")(
  ({ theme: { spacing, typography, palette }, color }) => ({
    [`&.${classes.content}`]: {
      padding: spacing(3, 1),
      paddingTop: 0,
      backgroundColor: palette.background.paper,
      boxSizing: "border-box",
      width: "550px",
    },
    [`& .${classes.contentContainer}`]: {},
    [`& .${classes.text}`]: {
      ...typography.body1,
      display: "inline-block",
    },
    [`& .${classes.text} a`]: {
      color: primary[500],
    },
    [`& .${classes.text} a:hover`]: {
      color: misc.purpleText,
    },

    [`& .${classes.title}`]: {
      ...typography.h6,
      color: palette.text.secondary,
      fontWeight: typography.fontWeightBold,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "normal",
      marginBottom: "6px",
      fontSize: "18px",
      marginTop: "8px",
    },
    [`& .${classes.icon}`]: {
      verticalAlign: "middle",
    },
    [`& .${classes.contentItemIcon}`]: {
      textAlign: "center",
    },
    [`& .${classes.grayIcon}`]: {
      color: palette.action.active,
      maxHeight: "20px",
    },
    [`& .${classes.colorfulContent}`]: {
      color: color || "",
    },
    [`& .${classes.lens}`]: {
      width: spacing(4.5),
      height: spacing(4.5),
      verticalAlign: "super",
    },
    [`& .${classes.textCenter}`]: {
      textAlign: "center",
    },
    [`& .${classes.dateAndTitle}`]: {
      lineHeight: 1.1,
    },
    [`& .${classes.titleContainer}`]: {
      paddingBottom: spacing(2),
    },
    [`& .${classes.container}`]: {
      paddingBottom: spacing(1.5),
    },
  })
);

export default function AppointmentPreview({
  appointmentData,
  formatDate,
}: DXAppointmentTooltip.ContentProps) {
  const [canAppointmentBeCompleted, setCanAppointmentBeCompleted] =
    useState<boolean>(false);
  const [hasCustomChecklist, setHasCustomChecklist] =
    useState<boolean>(false);
  const appointment = appointmentData as AppointmentModel;

  const timezone = appointment.clinic?.timezone;

  const creator = appointment?.created_by
    ? `${appointment?.created_by?.first_name} ${appointment?.created_by?.last_name}`.trim()
    : "";
  const createdDate =
    appointment?.created_at &&
    formatDateToDateAndTime(
      new Date(convertDateInTimezoneToCurrent(appointment.created_at, timezone))
    );

  const updatedBy = appointment?.updated_by
    ? `${appointment?.updated_by?.first_name} ${appointment?.updated_by?.last_name}`.trim()
    : "";
  const lastUpdateDate =
    appointment?.updated_at &&
    formatDateToDateAndTime(
      new Date(
        convertDateInTimezoneToCurrent(appointment?.updated_at, timezone)
      )
    );

  const color = appointmentData?.appointmentType?.color;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      const previewDiv = document.querySelector(
        `div[role="presentation"] div[data-testid="sentinelStart"] + .MuiPaper-root.MuiPaper-elevation.MuiPaper-rounded.MuiPaper-elevation8.MuiPopover-paper.Layout-popover.Layout-popover`
      ) as HTMLElement;

      if (previewDiv) {
        previewDiv.style.borderRadius = "8px";
        previewDiv.style.border = `solid 2px ${color}`;
      }
    }

    return () => {
      const previewDiv = document.querySelector(
        `div[role="presentation"] div[data-testid="sentinelStart"] + .MuiPaper-root.MuiPaper-elevation.MuiPaper-rounded.MuiPaper-elevation8.MuiPopover-paper.Layout-popover.Layout-popover`
      ) as HTMLElement;

      if (previewDiv) {
        previewDiv.style.borderRadius = "";
        previewDiv.style.border = "";
      }
    };
  }, [color]);

  return (
    <StyledTooltipContent
      ref={ref}
      data-testid="appointment-preview"
      className={classes.content}
      color={appointment?.appointmentType?.color}
      style={{
        boxSizing: "border-box",
      }}
    >
      <CreatedByBlock
        creator={creator}
        createdDate={createdDate}
        updatedBy={updatedBy}
        lastUpdateDate={lastUpdateDate}
      />
      {appointment.type === EVENT_TYPE_APPOINTMENT && (
        <>
          <AppointmentPreviewTitle
            appointmentData={appointment}
            formatDate={formatDate}
          />
          <AppointmentPreviewContent
            appointmentData={appointment}
            setCanAppointmentBeCompleted={setCanAppointmentBeCompleted}
            setHasCustomChecklist={setHasCustomChecklist}
          />
          <AppointmentPreviewAction
            appointmentData={appointment}
            canAppointmentBeCompleted={canAppointmentBeCompleted}
            hasCustomChecklist={hasCustomChecklist}
          />
        </>
      )}
      {[
        EVENT_TYPE_PERSONAL_EVENT,
        EVENT_TYPE_CLINIC_EVENT,
        EVENT_TYPE_PERSONAL_WORKSHIFT,
      ].includes(appointment.type) && (
        <>
          <PersonalEventPreview
            appointmentData={appointment as PersonalEvent}
            formatDate={formatDate}
          />
        </>
      )}
      {appointment.type === EVENT_TYPE_APPOINTMENT_BLOCK && (
        <>
          <AppointmentBlockPreview
            appointmentData={appointment as AppointmentBlock}
            formatDate={formatDate}
          />
        </>
      )}
    </StyledTooltipContent>
  );
}
